import React from "react";
import { Modal } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { CircularProgress } from "@mui/material";
import StyleDialog from "../scss/dialog.module.scss";
import StyleBtn from "../scss/btn.module.scss";

/**
 * Dialog reutilizable basado en la modal base.
 *
 * - No se puede cerrar por backdrop ni por tecla ESC (sin dismiss).
 * - Solo se cierra con la "X" o con los botones de acción.
 * - Configurable: 1 acción (solo confirmar) o 2 acciones (cancelar + confirmar).
 * - El contenido (icono + textos + inputs) se inyecta vía children.
 *
 * @param {boolean}  show           Muestra u oculta el diálogo.
 * @param {node}     children       Contenido central (icono, título, mensaje, inputs...).
 * @param {string}   confirmText    Texto del botón de confirmación.
 * @param {string}   cancelText     Texto del botón de cancelar (solo si showCancel).
 * @param {function} onConfirm      Handler del botón de confirmación.
 * @param {function} onCancel       Handler del botón de cancelar.
 * @param {function} onClose        Handler de la "X". Si no se pasa, usa onCancel u onConfirm.
 * @param {boolean}  showClose      Fuerza mostrar/ocultar la "X". Por defecto se infiere (visible si hay forma de cerrar).
 * @param {boolean}  confirmLoading Muestra loader en lugar del botón confirmar.
 * @param {boolean}  confirmDisabled Deshabilita el botón confirmar.
 * @param {string}   confirmClassName Override de clases del botón confirmar (default: btn azul oscuro de la app).
 * @param {string}   cancelClassName  Override de clases del botón cancelar (default: btn azul claro de la app).
 * @param {string}   width          Ancho del diálogo en desktop (ej. "30%", "480px").
 * @param {string}   widthTablet    Ancho en tablet.
 * @param {string}   widthMobile    Ancho en mobile.
 * @param {string}   className      Clases extra para el contenido.
 */
const DialogComponent = ({
    show = false,
    children,
    confirmText = "Accept",
    cancelText = "Close",
    onConfirm,
    onCancel,
    onClose,
    showClose,
    confirmLoading = false,
    confirmDisabled = false,
    cancelDisabled = false,
    confirmClassName = StyleBtn.btn,
    cancelClassName = `${StyleBtn.btnApp} ${StyleBtn.btnant}`,
    width,
    widthTablet,
    widthMobile,
    className = "",
}) => {
    const handleClose = onClose ?? onCancel ?? onConfirm;

    // Las acciones se infieren de la presencia de sus handlers.
    const hasCancel = typeof onCancel === "function";
    const hasConfirm = typeof onConfirm === "function";
    // La "X" se muestra por defecto si hay alguna forma de cerrar; se puede forzar con showClose.
    const displayClose = showClose ?? typeof handleClose === "function";

    const cssVars = {
        ...(width ? { "--dialog-width": width } : {}),
        ...(widthTablet ? { "--dialog-width-tablet": widthTablet } : {}),
        ...(widthMobile ? { "--dialog-width-mobile": widthMobile } : {}),
    };

    return (
        <Modal
            show={show}
            backdrop="static"
            keyboard={false}
            centered
            style={cssVars}
            dialogClassName={StyleDialog.dialog}
            contentClassName={`${StyleDialog.content} ${className}`}
        >
            <div className={StyleDialog.header}>
                {displayClose && (
                    <button
                        type="button"
                        aria-label="close"
                        className={StyleDialog.closeBtn}
                        onClick={handleClose}
                    >
                        <Icon icon="mdi:close" className={StyleDialog.closeIcon} />
                    </button>
                )}
            </div>

            <div className={StyleDialog.body}>{children}</div>

            {(hasCancel || hasConfirm) && (
                <div className={StyleDialog.footer}>
                    {hasCancel && (
                        <button
                            type="button"
                            disabled={cancelDisabled}
                            className={`Poppins fw_500 ${cancelClassName}`}
                            onClick={onCancel}
                        >
                            {cancelText}
                        </button>
                    )}

                    {hasConfirm && (
                        confirmLoading ? (
                            <div className={StyleDialog.loaderInline}>
                                <CircularProgress size={28} />
                            </div>
                        ) : (
                            <button
                                type="button"
                                disabled={confirmDisabled}
                                className={`Poppins fw_500 ${confirmClassName}`}
                                onClick={onConfirm}
                            >
                                {confirmText}
                            </button>
                        )
                    )}
                </div>
            )}
        </Modal>
    );
};

export default DialogComponent;
